import * as UI from 'ui';
import * as TranslatedComponents from 'app/area/shared/translated-components';
import * as AddFeature from 'app/area/case/features';
import * as Select from 'app/area/case/selectors';
import { ControlType } from 'app/infrastructure/ui/component-configuration';
import { Dummy } from 'app/infrastructure/ui/debug-render';
import * as AppEnhance from 'app/components/enhancers';
import * as UIEnhance from 'ui/common/enhancers';

import { tableName, columnName } from 'app/area/case/case-data-model';
import * as caseComponents from 'app/area/case/components';
import * as appComponents from 'app/components';
import { IteratorType } from 'app/infrastructure/ui';
import { ComponentIndex } from 'app/infrastructure/ui/types-uischema';
import { createUTCDate } from 'app/infrastructure/data/date';
import { caseSignReadonly } from 'app/area/case/components/enhancers/case-sign-readonly';
import { readonlyWhenSigned } from 'app/area/case/components/enhancers/readonly-when-signed';
import { withManageOptions } from 'app/area/case/components/enhancers/manage-options';
import { SharePreset } from 'app/area/case/features';
import { withCopyRiskEvaluation, withGetUiDefinition } from 'app/area/case/features/loss-potential-evaluation';

const Enhance = { ...AppEnhance, ...UIEnhance };

export const editModeComponents: ComponentIndex = {

  [ControlType.NotImplemented]: {
    component: Dummy,
    configType: ControlType.NotImplemented
  },
  [ControlType.MainView]: {
    component: UI.Paper,
    selectors: [
      Select.activeSectionState,
      Select.nextAndPreviousSection,
      Select.caseId,
      Select.fieldValues([{ dataTag: 'Title', fieldName: 'title' }])
    ],
    enhancers: [
      Enhance.withDocumentTitleFromProps('title.valueObject.value', 'caseId'),
      Enhance.withSectionKeyboardPaging()
    ],
    configType: ControlType.MainView
  },

  [ControlType.TextBox]: {
    component: UI.Textbox,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.TextBox
  },

  [ControlType.CheckBox]: {
    component: UI.Checkbox,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.CheckBox
  },

  [ControlType.NumberBox]: {
    component: UI.Numberbox,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.NumberBox
  },

  [ControlType.TextArea]: {
    component: UI.Textarea,
    enhancers: [Enhance.withSize({ xl: 8 })],
    configType: ControlType.TextArea
  },

  [ControlType.SelectSingle]: {
    component: TranslatedComponents.SelectSingle,
    selectors: [Select.primaryKeyFromContext(), Select.manageOptions],
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ md: 6, xl: 4 }), Enhance.withTileSize({ sm: 6, xl: 4 }),
      withManageOptions],
    configType: ControlType.SelectSingle
  },

  [ControlType.SelectSingleHierarchy]: {
    component: TranslatedComponents.SelectSingleHierarchy,
    selectors: [Select.primaryKeyFromContext(), Select.manageOptions],
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ md: 6, xl: 4 }), withManageOptions],
    configType: ControlType.SelectSingleHierarchy
  },

  [ControlType.SelectMultiple]: {
    component: TranslatedComponents.SelectMultiple,
    selectors: [Select.primaryKeyFromContext(), Select.manageOptions],
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ md: 6, xl: 4 }), withManageOptions],
    configType: ControlType.SelectMultiple
  },

  [ControlType.SelectMultipleHierarchy]: {
    component: TranslatedComponents.SelectMultipleHierarchy,
    selectors: [Select.primaryKeyFromContext(), Select.manageOptions],
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ md: 6, xl: 4 }), withManageOptions],
    configType: ControlType.SelectMultipleHierarchy
  },

  [ControlType.DateTime]: {
    component: UI.DateTime,
    enhancers: [Enhance.withSize({ lg: 6, xl: 8 })],
    configType: ControlType.DateTime
  },

  [ControlType.Date]: {
    component: UI.DateTime,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 }), Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.Date
  },

  [ControlType.TileView]: {
    component: UI.TileView,
    configType: ControlType.TileView,
    selectors: [
      Select.activeChildItem,
      Select.enabledChildEntityOperations
    ],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.Tile]: {
    component: UI.Tile,
    selectors: [
      Select.validationDescendants,
      Select.primaryKeyFromContext(),
      Select.copyIteratorProps,
      Select.editDocumentItemVisibility,
      Select.enabledChildEntityOperations
    ],
    configType: ControlType.Tile
  },

  [ControlType.SelectSingleShortList]: {
    component: UI.SelectSingleShortList,
    selectors: [Select.primaryKeyFromContext()],
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ xl: 8 })],
    configType: ControlType.SelectSingleShortList
  },

  [ControlType.EntityKeyCategorySelectSingleShortList]: {
    component: UI.SelectSingleShortList,
    selectors: [Select.primaryKeyFromContext()],
    enhancers: [
      Enhance.withPrimaryKeyAsOptionCategoryId,
      Enhance.withPrimaryKeyAsOptionIteratorId,
      Enhance.withSize({ xl: 8 })
    ],
    configType: ControlType.EntityKeyCategorySelectSingleShortList
  },

  [ControlType.CommentTileView]: {
    component: appComponents.CommentTileView,
    configType: ControlType.CommentTileView,
    iterate: { type: IteratorType.DataRows },
    enhancers: [Enhance.withSize({ lg: 8 })],
    selectors: [Select.commentValues, Select.activeChildItem, Select.enabledChildEntityOperations]
  },

  [ControlType.CommentTile]: {
    component: appComponents.CommentTile,
    configType: ControlType.CommentTile,
    selectors: [Select.enabledChildEntityOperations]
  },

  [ControlType.SliderRating]: {
    component: UI.SliderRating,
    enhancers: [Enhance.withSize({ lg: 12 }), Enhance.withNotSelectedOptionIncluded],
    configType: ControlType.SliderRating
  },

  [ControlType.EntityKeyCategorySliderRating]: {
    component: UI.SliderRating,
    selectors: [Select.primaryKeyFromContext()],
    enhancers: [
      Enhance.withPrimaryKeyAsOptionCategoryId,
      Enhance.withPrimaryKeyAsOptionIteratorId,
      Enhance.withNotSelectedOptionIncluded,
      Enhance.withSize({ lg: 12 })
    ],
    configType: ControlType.EntityKeyCategorySliderRating
  },

  [ControlType.ButtonList]: {
    component: UI.ButtonList,
    enhancers: [
      Enhance.withSize({ lg: 12 }),
      AddFeature.withTriggerValidation
    ],
    configType: ControlType.ButtonList
  },

  [ControlType.HeaderPanel]: {
    component: appComponents.EditHeaderPanel,
    selectors: [
      Select.sharedState,
      Select.headerVisibilityConfiguration,
      Select.activeSectionState,
      Select.firstSectionId,
      Select.changeCaseTypeActionCreator,
      Select.expandAll,
      Select.domainMetadata(100, 'readOnly'),
      Select.caseId
    ],
    enhancers: [
      Enhance.withClassOnFirstTab(true),
      Enhance.withProperty('enableStickyHeader', true),
      AddFeature.withSaveAsDraft
    ],
    configType: ControlType.HeaderPanel
  },

  [ControlType.DisplayDate]: {
    component: UI.DisplayDate,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayDate
  },

  [ControlType.DisplayNumber]: {
    component: UI.DisplayNumber,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 }), Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayNumber
  },

  [ControlType.DisplayNumberUnitOfMeasure]: {
    component: appComponents.DisplayNumberUnitOfMeasure,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    selectors: [Select.childValues],
    configType: ControlType.DisplayNumberUnitOfMeasure
  },

  [ControlType.DisplayText]: {
    component: UI.DisplayText,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayText
  },

  [ControlType.DisplayCodes]: {
    component: UI.DisplayCodes,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayCodes
  },

  [ControlType.DisplayCodesUnitOfMeasure]: {
    component: appComponents.DisplayCodesUnitOfMeasure,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayCodesUnitOfMeasure
  },

  [ControlType.DisplayCodesHierarchy]: {
    component: UI.DisplayCodesHierarchy,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayCodesHierarchy
  },

  [ControlType.DisplayBoolean]: {
    component: UI.DisplayBoolean,
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayBoolean
  },

  [ControlType.LinkHeading]: {
    component: UI.ExternalLinkHeading,
    selectors: [Select.urlToConnectedCase({ withBaseUrl: true }), Select.expandAll],
    enhancers: [Enhance.withClassNames('compact', true)],
    configType: ControlType.LinkHeading
  },

  [ControlType.ExpandableText]: {
    component: UI.ExpandableText,
    configType: ControlType.ExpandableText
  },

  [ControlType.ProcessPanel]: {
    component: UI.ProcessPanel,
    selectors: [
      Select.validationDescendants,
      Select.invalidSectionIds,
      Select.activeSectionState,
      Select.nextAndPreviousSection,
      Select.validState
    ],
    enhancers: [Enhance.withRenderActiveSection(), Enhance.withSectionTab()],
    configType: ControlType.ProcessPanel
  },

  [ControlType.GroupPanel]: {
    component: UI.GroupPanel,
    enhancers: [Enhance.withSize({ md: 12 })],
    configType: ControlType.GroupPanel
  },

  [ControlType.FlexPanel]: {component: UI.Hidden,
    configType: ControlType.FlexPanel
  },

  [ControlType.FluidPanel]: {
    component: UI.FluidPanel,
    configType: ControlType.FluidPanel
  },

  [ControlType.ColumnsPanel]: {
    component: UI.ColumnsPanel,
    configType: ControlType.ColumnsPanel
  },

  [ControlType.DetailForm]: {
    component: UI.DetailFormPanel,
    configType: ControlType.DetailForm
  },

  [ControlType.ConnectedCasesTileView]: {
    component: caseComponents.ConnectedCasesTileViewEdit,
    selectors: [
      Select.entityId, Select.caseId,
      Select.enabledChildEntityOperations,
      Select.primaryKeyFromContextExpanded('connectionTypeId'),
      Select.connectedDrafts,
      Select.connectedCasesIds,
      Select.connectedCasesPendingDeleteIds,
      Select.favouriteComponent,
      Select.activeChildItem
    ],
    enhancers: [
      AddFeature.withConnectedCases,
      AddFeature.withSearchCasesToConnect,
      AddFeature.withConnectedCasePermissions,
      AddFeature.withCallsToActions,
      Enhance.withLatestEtag
    ],
    configType: ControlType.ConnectedCasesTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.ConnectedCasesTile]: {
    component: caseComponents.ConnectedCasesTile,
    selectors: [
      Select.validationDescendants,
      Select.enabledChildEntityOperations,
      Select.primaryKeyFromContext(),
      Select.primaryKeyFromContextExpanded('connectionTypeId'),
      Select.entityId,
      Select.connectedCasesMaxSequence,
      Select.connectedDrafts,
      Select.connectedCasePermissions
    ],
    enhancers: [
      AddFeature.withConnectionSequenceUpdate, 
      Enhance.withProperty('urlOpenNew', true),
      AddFeature.withConnectedCasePermissions
    ],
    configType: ControlType.ConnectedCasesTile
  },

  [ControlType.DisplayChildValues]: {
    component: UI.DisplayChildValues,
    selectors: [Select.childValues],
    enhancers: [Enhance.withTileSize({ sm: 6, xl: 4 })],
    configType: ControlType.DisplayChildValues
  },

  [ControlType.FileUploader]: {
    component: UI.FileUploader,
    enhancers: [AddFeature.withFileUpload, AddFeature.withFileDownload()],
    configType: ControlType.FileUploader
  },

  [ControlType.CodeSelectorTileView]: {
    component: TranslatedComponents.CodeSelectorTileView,
    selectors: [
      Select.primaryKeyFromContextExpanded('categoryId'),
      Select.enabledChildEntityOperations,
      Select.childEntityTypeKeys,
      Select.activeChildItem,
      Select.manageOptions
    ],
    enhancers: [withManageOptions],
    configType: ControlType.CodeSelectorTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.CodeTitleTile]: {
    component: appComponents.CodeTitleTile,
    selectors: [Select.validationDescendants, Select.primaryKeyFromContext(), Select.enabledChildEntityOperations],
    configType: ControlType.CodeTitleTile
  },

  [ControlType.LossPotentialEvaluation]: {
    component: caseComponents.LossPotentialEvaluation,
    selectors: [Select.primaryKeyFromContext(), Select.validationDescendants,  Select.editFooterDetailsButtonVisibility],
    configType: ControlType.LossPotentialEvaluation
  },

  [ControlType.LossPotentialEvaluationTileView]: {
    component: caseComponents.LossPotentialEvaluationTileView,
    selectors: [
      Select.enabledChildEntityOperations,
      Select.childEntities,
      Select.lossPotentialEvaluationValues    ],
    enhancers: [withCopyRiskEvaluation(), withGetUiDefinition, Enhance.withProperty('showCompleteEvaluationInfo', true)],
    configType: ControlType.LossPotentialEvaluationTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.LossPotentialConsequenceEvaluation]: {
    component: caseComponents.LossPotentialConsequenceEvaluation,
    selectors: [Select.consequenceRiskValues],
    configType: ControlType.LossPotentialConsequenceEvaluation
  },

  [ControlType.LossPotentialConsequenceEvaluationTileView]: {
    component: TranslatedComponents.CodeSelectorTileView,
    selectors: [
      Select.childEntityTypeKeys,
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    enhancers: [withManageOptions, Enhance.withProperty('rowClassName', 'col-12')],
    configType: ControlType.LossPotentialConsequenceEvaluationTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.LossPotentialConsequenceEvaluationTileRiskMatrixPanel]: {
    component: UI.ColumnsPanel,
    enhancers: [Enhance.withProperty('columns', '4')],
    configType: ControlType.LossPotentialConsequenceEvaluationTileRiskMatrixPanel
  },

  [ControlType.LossPotentialConsequenceEvaluationTileDetailPanel]: {
    component: UI.ColumnsPanel,
    enhancers: [Enhance.withProperty('columns', '8')],
    configType: ControlType.LossPotentialConsequenceEvaluationTileDetailPanel
  },

  [ControlType.CauseCategoryTileView]: {
    component: appComponents.CategoryTileView,
    configType: ControlType.CauseCategoryTileView,
    enhancers: [Enhance.withOptionLoading<number>()],
    selectors: [Select.activeChildItem],
    iterate: { type: IteratorType.CodeTable }
  },

  [ControlType.CheckListCategoryTileView]: {
    component: appComponents.CategoryTileView,
    selectors: [
      Select.childEntityTypeKeys,
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    configType: ControlType.CheckListCategoryTileView,
    enhancers: [Enhance.withOptionLoading<number>(), withManageOptions],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.CheckListCategoryTile]: {
    component: appComponents.CategoryTile,
    selectors: [Select.validationDescendants, Select.primaryKeyFromContext()],
    configType: ControlType.CheckListCategoryTile
  },

  [ControlType.CheckListEvaluationPanel]: {
    component: UI.FlexPanel,
    configType: ControlType.CheckListEvaluationPanel,
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('value')]
  },

  [ControlType.CategoryTileView]: {
    component: appComponents.CategoryTileView,
    configType: ControlType.CategoryTileView,
    enhancers: [Enhance.withOptionLoading<number>()],
    selectors: [
      Select.activeChildItem,
      Select.enabledChildEntityOperations
    ],
    iterate: { type: IteratorType.CodeTable }
  },

  [ControlType.CategoryTile]: {
    component: appComponents.CategoryTile,
    selectors: [
      Select.validationDescendants,
      Select.enabledChildEntityOperations,
      Select.primaryKeyFromContext()
    ],
    configType: ControlType.CategoryTile
  },

  [ControlType.DisplayNumericHeader]: {
    component: UI.DisplayNumericHeader,
    enhancers: [Enhance.withSize({ lg: 12 })],
    configType: ControlType.DisplayNumericHeader
  },

  [ControlType.ConsequenceTypeTileView]: {
    component: TranslatedComponents.CodeSelectorTileView,
    selectors: [Select.childEntityTypeKeys, Select.activeChildItem, Select.enabledChildEntityOperations, Select.manageOptions],
    enhancers: [withManageOptions],
    configType: ControlType.ConsequenceTypeTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.ConsequenceTypeTile]: {
    component: appComponents.CodeTitleTile,
    selectors: [Select.validationDescendants, Select.primaryKeyFromContext()],
    configType: ControlType.ConsequenceTypeTile
  },

  [ControlType.ConsequenceTileView]: {
    component: UI.TileView,
    selectors: [
      Select.primaryKeyFromContextExpanded('consequenceType'),
      Select.activeChildItem,
      Select.enabledChildEntityOperations
    ],
    configType: ControlType.ConsequenceTileView,
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('consequenceType')],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.SectionPanel]: {
    component: UI.SectionPanel,
    selectors: [
      Select.validationDescendants,
      Select.invalidSectionIds,
      Select.firstSectionId,
      Select.activeSectionState,
      Select.nextAndPreviousSection,
      Select.validState
    ],
    enhancers: [
      Enhance.withRenderActiveSection(),
      Enhance.withSectionTab()
    ],
    configType: ControlType.SectionPanel
  },

  [ControlType.FlexRowPanel]: {
    component: UI.FlexRowPanel,
    configType: ControlType.FlexRowPanel
  },

  [ControlType.LongDescription]: {
    component: UI.LongDescription,
    configType: ControlType.LongDescription
  },

  [ControlType.SectionSummaryPanel]: {
    component: caseComponents.SectionSummary,
    enhancers: [Enhance.withSize({ md: 12 })],
    configType: ControlType.SectionSummaryPanel
  },

  [ControlType.TileTopZone]: {
    component: UI.TileTopZone,
    configType: ControlType.TileTopZone
  },

  [ControlType.DisplayNumericSum]: {
    component: UI.DisplayNumber,
    selectors: [Select.childValuesTotals('valueItem')],
    configType: ControlType.DisplayNumericSum
  },

  [ControlType.AttachLinkTileView]: {
    component: caseComponents.AttachLinkTileView,
    selectors: [Select.enabledChildEntityOperations, Select.attachLinkValues, Select.activeChildItem],
    configType: ControlType.AttachLinkTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.DisplaySafeAnchor]: {
    component: appComponents.DisplaySafeAnchor,
    configType: ControlType.DisplaySafeAnchor
  },

  [ControlType.AttachLinkTile]: {
    component: caseComponents.AttachLinkTile,
    selectors: [Select.enabledChildEntityOperations, Select.fieldValues([
      {
        fieldName: (columnName[tableName.link]).anchorIndicator,
        dataTag: (columnName[tableName.link]).linkUrl
      }])],
    configType: ControlType.AttachLinkTile
  },

  [ControlType.SubstanceCategoryTileView]: {
    component: appComponents.CategoryTileView,
    configType: ControlType.SubstanceCategoryTileView,
    enhancers: [Enhance.withOptionLoading<number>()],
    selectors: [Select.activeChildItem],
    iterate: { type: IteratorType.CodeTable }
  },

  [ControlType.EnvironmentalValuesTileView]: {
    component: caseComponents.PreselectedCodeTileView,
    selectors: [
      Select.primaryKeyFromContextExpanded('categoryId'),
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    configType: ControlType.EnvironmentalValuesTileView,
    enhancers: [
      Enhance.withPrimaryKeyAsOptionIteratorId,
      Enhance.withRowFieldInitialValue(columnName[tableName.green].envValSubstanceCategory, 'categoryId'),
      withManageOptions
    ],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.SafeAnchor]: {
    component: appComponents.SafeAnchor,
    enhancers: [Enhance.withSize({ xl: 8 })],
    configType: ControlType.SafeAnchor
  },

  [ControlType.InteractionTileView]: {
    component: appComponents.CategoryTileView,
    configType: ControlType.InteractionTileView,
    selectors: [Select.primaryKeyFromContextExpanded('categoryId'), Select.activeChildItem],
    enhancers: [Enhance.withOptionLoading<number>()],
    iterate: { type: IteratorType.CodeTable }
  },

  [ControlType.InteractionTileViewDrugReaction]: {
    component: UI.TileView,
    configType: ControlType.InteractionTileViewDrugReaction,
    enhancers: [Enhance.withOptionLoading<number>()],
    selectors: [
      Select.activeChildItem,
      Select.enabledChildEntityOperations
    ],
    iterate: { type: IteratorType.CodeTable }
  },

  [ControlType.InteractionTile]: {
    component: appComponents.CompactCodeTitleTile,
    selectors: [Select.validationDescendants, Select.primaryKeyFromContext(), Select.enabledChildEntityOperations, Select.childValues],
    configType: ControlType.InteractionTile
  },

  [ControlType.CausesTileView]: {
    component: TranslatedComponents.CodeSelectorTileView,
    selectors: [
      Select.primaryKeyFromContextExpanded('categoryId'),
      Select.childEntityTypeKeys,
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    enhancers: [withManageOptions],
    configType: ControlType.CausesTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.CompactCodeTitleTile]: {
    component: appComponents.CompactCodeTitleTile,
    selectors: [Select.validationDescendants, Select.primaryKeyFromContext()],
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.CompactCodeTitleTile
  },

  [ControlType.SummaryTileView]: {
    component: caseComponents.SummaryTileView,
    configType: ControlType.SummaryTileView,
    selectors: [Select.activeChildItem],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.TitleWrapperPrimaryKey]: {
    component: appComponents.TitleWrapper,
    selectors: [Select.primaryKeyFromContext('title')],
    configType: ControlType.TitleWrapperPrimaryKey
  },

  [ControlType.ReferenceTileView]: {
    component: TranslatedComponents.ReferenceSelectTileView,
    selectors: [
      Select.referenceCodeDataTag,
      Select.referenceModalTempData,
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    configType: ControlType.ReferenceTileView,
    enhancers: [Enhance.withPrimaryKeyAsOptionIteratorId, Enhance.withSize({ lg: 12 }), withManageOptions],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.ReferenceTile]: {
    component: appComponents.CompactCodeTitleTile,
    selectors: [
      Select.validationDescendants,
      Select.referenceCodeDataTag,
      Select.referenceFieldValueAsPrimaryKey,
      Select.enabledChildEntityOperations
    ],
    configType: ControlType.ReferenceTile,
    enhancers: []
  },

  [ControlType.DisplayCodesReference]: {
    component: UI.DisplayCodes,
    enhancers: [Enhance.withTileSize({ md: 6, xl: 4 })],
    configType: ControlType.DisplayCodesReference
  },

  [ControlType.ReferenceDate]: {
    component: UI.DateTime,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'),
    Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceDate
  },

  [ControlType.ReferenceDateTime]: {
    component: UI.DateTime,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'),
    Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceDateTime
  },

  [ControlType.ReferenceTextBox]: {
    component: UI.Textbox,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceTextBox
  },

  [ControlType.ReferenceNumberBox]: {
    component: UI.Numberbox,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceNumberBox
  },

  [ControlType.ReferenceEntityKeyCategorySelectSingleShortList]: {
    component: UI.SelectSingleShortList,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceEntityKeyCategorySelectSingleShortList
  },

  [ControlType.ReferenceCheckBox]: {
    component: UI.Checkbox,
    selectors: [Select.referenceFieldValue],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.ReferenceCheckBox
  },

  [ControlType.ReferenceSelectSingleHierarchy]: {
    component: TranslatedComponents.SelectSingleHierarchy,
    selectors: [
      Select.referenceFieldValue,
      Select.manageOptions
    ],
    enhancers: [
      Enhance.withOptionCategorySupport,
      Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 }),
      withManageOptions
    ],
    configType: ControlType.ReferenceSelectSingleHierarchy
  },

  [ControlType.ReferenceSelectSingle]: {
    component: TranslatedComponents.SelectSingle,
    selectors: [Select.referenceFieldValue, Select.manageOptions],
    enhancers: [Enhance.withHideIfPropValueNotInVisibleForValues('referenceType'), Enhance.withSize({ md: 6, xl: 4 }),
      withManageOptions
    ],
    configType: ControlType.ReferenceSelectSingle
  },

  [ControlType.SurveyLink]: {
    component: UI.Numberbox,
    selectors: [],
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.SurveyLink
  },

  [ControlType.DisplaySurveyLink]: {
    component: UI.Link,
    selectors: [Select.templateURL],
    enhancers: [Enhance.withSize({ md: 6, lg: 4, xl: 3 })],
    configType: ControlType.SurveyLink
  },

  [ControlType.Link]: {
    component: UI.Link,
    selectors: [Select.domainTemplateUrl],
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    configType: ControlType.Link
  },

  [ControlType.Signature]: {
    component: UI.ToggleButton,
    selectors: [Select.rowEntity, Select.currentUser],
    enhancers: [
      Enhance.withProperty('valueResolver', () => createUTCDate()),
      caseSignReadonly(),
      Enhance.withSize({ md: 6, xl: 4 })
    ],
    configType: ControlType.Signature
  },

  [ControlType.RiskMatrix]: {
    component: appComponents.RiskMatrix,
    selectors: [Select.validationDescendants],
    enhancers: [AddFeature.withRiskMatrix],
    configType: ControlType.RiskMatrix
  },

  [ControlType.DisplayInsuranceBalance]: {
    component: UI.DisplayNumber,
    selectors: [Select.diffFieldValueAndChildValuesTotals('valueItem', (columnName[tableName.economy]).costEstimate)],
    configType: ControlType.DisplayInsuranceBalance
  },

  [ControlType.IndicatorCodeSelectorTileView]: {
    component: TranslatedComponents.CodeSelectorTileView,
    selectors: [
      Select.childEntityTypeKeys,
      Select.activeChildItem,
      Select.enabledChildEntityOperations,
      Select.manageOptions
    ],
    enhancers: [withManageOptions],
    configType: ControlType.IndicatorCodeSelectorTileView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.AddDocumentItemLinkButton]: {
    component: appComponents.AddDocumentItemLinkButton,
    selectors: [Select.connectedPkFromContext],
    configType: ControlType.AddDocumentItemLinkButton
  },

  [ControlType.ProcessingLogView]: {
    component: appComponents.LogView,
    configType: ControlType.ProcessingLogView,
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.ProcessingLogDetails]: {
    component: appComponents.LogDetails,
    configType: ControlType.ProcessingLogDetails
  },

  [ControlType.ProcessingLogLink]: {
    component: appComponents.ProcessingLogLink,
    selectors: [Select.childEntityCount([
      {
        entityPath: [{ entityType: tableName.trans }, { entityType: tableName.transLog }],
        propName: (columnName[tableName.transLog] as any).caseLogs,
        dataTag: (columnName[tableName.transLog] as any).CaseLogNo
      }])],
    enhancers: [Enhance.withSize({ md: 4, lg: 6, xl: 6 })],
    configType: ControlType.ProcessingLogLink
  },

  [ControlType.MailLogLink]: {
    component: appComponents.MailLogLink,
    configType: ControlType.MailLogLink
  },

  [ControlType.CaseLogLink]: {
    component: appComponents.CaseLogLink,
    enhancers: [AddFeature.withCaseLogDownload],
    selectors: [Select.childValues, Select.primaryKeyFromContext()],
    configType: ControlType.CaseLogLink
  },

  [ControlType.MailLogDetails]: {
    component: appComponents.LogDetails,
    configType: ControlType.MailLogDetails
  },

  [ControlType.AddNewCaseButton]: {
    component: caseComponents.AddNewCaseButtonEdit,
    selectors: [
      Select.entityId, Select.connectedCasesMaxSequence, Select.connectedDrafts
    ],
    enhancers: [Enhance.withLatestEtag],
    configType: ControlType.AddNewCaseButton
  },

  [ControlType.CompactTileHeader]: {
    component: appComponents.CompactTileHeader,
    configType: ControlType.CompactTileHeader,
    selectors: [Select.tileHeaderPrimaryKey]
  },

  [ControlType.ConnectedCasesFactbox]: {
    component: caseComponents.ConnectedCasesFactbox,
    configType: ControlType.ConnectedCasesFactbox,
    enhancers: [Enhance.withClassNames('compact')],
    selectors: [
      Select.connectedCasesRiskValues
    ]
  },

  [ControlType.CheckForDuplicateRegistrationsButton]: {
    component: caseComponents.CheckForDuplicateRegistrationsButton,
    selectors: [
      Select.entityId,
      Select.connectedCasesMaxSequence,
      Select.requiredSearchFieldsFilled,
      Select.connectedCasesIds,
      Select.connectedCasesPendingDeleteIds,
      Select.favouriteComponent
    ],
    enhancers: [
      AddFeature.withSearchCasesToConnect
    ],
    configType: ControlType.CheckForDuplicateRegistrationsButton
  },

  [ControlType.LinkSearchExternalProvider]: {
    component: caseComponents.SearchExternalProvider,
    configType: ControlType.LinkSearchExternalProvider,
    enhancers: [AddFeature.withSearchExternalProvider]
  },

  [ControlType.CompactTile]: {
    component: UI.CompactTile,
    selectors: [
      Select.validationDescendants,
      Select.copyIteratorProps,
      Select.editDocumentItemVisibility,
      Select.primaryKeyFromContext(),
      Select.enabledChildEntityOperations,
      Select.editDetailsActionOverride
    ],
    enhancers: [Enhance.withProperty('shouldRenderSubmitButton', true), Enhance.withProperty('showDetailsHeader', true)],
    configType: ControlType.CompactTile
  },

  [ControlType.ConnectToBowtieButton]: {
    component: caseComponents.ConnectToBowtieButton,
    selectors: [
      Select.entityId,
      Select.connectedCasesMaxSequence,
      Select.requiredSearchFieldsFilled,
      Select.connectedCasesIds,
      Select.connectedCasesPendingDeleteIds,
      Select.favouriteComponent
    ],
    enhancers: [
      AddFeature.withSearchCasesToConnect
    ],
    configType: ControlType.ConnectToBowtieButton
  },

  [ControlType.BarrierInspectionTileView]: {
    component: caseComponents.SummaryTileView,
    configType: ControlType.BarrierInspectionTileView,
    selectors: [Select.activeChildItem],
    enhancers: [Enhance.withProperty('doubleWidth', true)],
    iterate: { type: IteratorType.DataRows }
  },

  [ControlType.DisplayNumberBarrierInspection]: {
    component: UI.DisplayNumber,
    enhancers: [Enhance.withSize({ lg: 12 })],
    configType: ControlType.DisplayNumber
  },

  [ControlType.DisplayCodesStatus]: {
    component: UI.DisplayCodes,
    enhancers: [Enhance.withProperty('displayBackgroundColor', 'false'),
    Enhance.withProperty('style', 'primary')],
    configType: ControlType.DisplayCodesStatus
  },

  [ControlType.SelectSingleSignature]: {
    component: TranslatedComponents.SelectSingle,
    selectors: [
      Select.rowEntity,
      Select.primaryKeyFromContext()
    ],
    enhancers: [
      readonlyWhenSigned(),
      Enhance.withPrimaryKeyAsOptionIteratorId,
      Enhance.withSize({ md: 6, xl: 4 })
    ],
    configType: ControlType.SelectSingleSignature
  },

  [ControlType.DisplayEnvironmentalPreviousValues]: {
    component: caseComponents.DisplayEnvironmentalPreviousValuesComponent,
    enhancers: [Enhance.withSize({ md: 6, xl: 4 })],
    selectors: [Select.previousEnvironmentalValues, Select.sectionLinks],
    configType: ControlType.DisplayEnvironmentalPreviousValues
  },

  [ControlType.ShareLinkButton]: {
    component: caseComponents.ShareLinkButton,
    selectors: [Select.sharedState],
    configType: ControlType.ShareLinkButton
  },

  [ControlType.MultiSelectRecipients]: {
    component: caseComponents.MultiSelectRecipients,
    enhancers: [AddFeature.withSharing(SharePreset.draft)],
    selectors: [Select.entityId],
    configType: ControlType.MultiSelectRecipients
  },

  [ControlType.BadgeListFileUploader]: {
    component: caseComponents.BadgeListFileUploader,
    enhancers: [AddFeature.withFileUpload, AddFeature.withFileDownload()],
    configType: ControlType.BadgeListFileUploader
  },

  [ControlType.Thumbnail]: {
    component: caseComponents.Thumbnail,
    enhancers: [AddFeature.withFileDownload(), AddFeature.withFileUpload, Enhance.withClassNames('thumbnail-edit')],
    configType: ControlType.Thumbnail
  },

  [ControlType.DetailsPanelHeader]: {
    component: UI.DetailFormPanelHeader,
    selectors: [Select.primaryKeyFromContext()],
    configType: ControlType.DetailsPanelHeader
  },

  [ControlType.LinkButtonFileUploader]: {
    component: caseComponents.LinkButtonFileUploader,
    enhancers: [AddFeature.withFileUpload, AddFeature.withFileDownload()],
    configType: ControlType.LinkButtonFileUploader
  },

  [ControlType.ChangeLogLink]: {
    component: appComponents.ChangeLogLink,
    selectors: [Select.caseLogValues, Select.primaryKeyFromContext(), Select.caseId],
    configType: ControlType.ChangeLogLink
  },

  [ControlType.LossPotentialEvaluationFooter]: {
    component: caseComponents.LossPotentialEvaluationFooter,
    configType: ControlType.LossPotentialEvaluationFooter
  },

  [ControlType.LossPotentialConsequenceEvaluationFooter]: {
    component: caseComponents.LossPotentialConsequenceEvaluationFooter,
    selectors: [Select.childValues],
    configType: ControlType.LossPotentialConsequenceEvaluationFooter
  },

  [ControlType.ButtonGroupPanel]: {
    component: UI.ButtonGroupPanel,
    selectors: [Select.childEntitiesReadOnly],
    configType: ControlType.ButtonGroupPanel
  },

    [ControlType.ConnectedCasesBowtieFactItem]: {
      component: caseComponents.ConnectedCasesBowtieFactItem,
      configType: ControlType.ConnectedCasesBowtieFactItem,
      selectors: [
        Select.connectedCasesBowtieSupported,
        Select.primaryKeyFromContext()
      ]
    },      
    
    [ControlType.ConnectedCasesRiskFactItem]: {
      component: caseComponents.ConnectedCasesRiskFactItem,
      configType: ControlType.ConnectedCasesRiskFactItem,
      selectors: [
        Select.connectedCasesRiskValues,
        Select.urlToConnectedCase({ withBaseUrl: false, openNew: true }),
        Select.caseTypeLinkDomain
      ]
    },
  
    [ControlType.ConnectedCasesResidualRiskFactItem]: {
      component: caseComponents.ConnectedCasesResidualRiskFactItem,
      configType: ControlType.ConnectedCasesResidualRiskFactItem,
      selectors: [
        Select.connectedCasesResidualRiskValues,
        Select.urlToConnectedCase({ withBaseUrl: false, openNew: true })
      ]
    },
  
    [ControlType.ConnectedCasesIntegrityStatusFactItem]: {
      component: caseComponents.ConnectedCasesIntegrityStatusFactItem,
      configType: ControlType.ConnectedCasesIntegrityStatusFactItem,
      selectors: [
        Select.connectedCasesIntegrityValues,
        Select.urlToConnectedCase({ withBaseUrl: false, openNew: true }),
        Select.caseTypeLinkDomain
      ]
    },
  
    [ControlType.ConnectedCasesIntegrityStatusBarrierEvalFactItem]: {
      component: caseComponents.ConnectedCasesIntegrityStatusBarrierEvalFactItem,
      configType: ControlType.ConnectedCasesIntegrityStatusBarrierEvalFactItem,
      selectors: [
        Select.connectedCasesintegrityStatusBarrierEvalValues,
        Select.urlToConnectedCase({ withBaseUrl: false, openNew: true })
      ]
    },
  
    [ControlType.ConnectedCasesActionFactItem]: {
      component: caseComponents.ConnectedCasesActionFactItem,
      configType: ControlType.ConnectedCasesActionFactItem,
      selectors: [
        Select.connectedCasesActionCount,
        Select.urlToConnectedCase({ withBaseUrl: false, openNew: true })
      ]
    },    
    [ControlType.DisplayLink]: {
      component: UI.DisplayLink,
      enhancers: [Enhance.withProperty('urlOpenNew', true)],
      configType: ControlType.DisplayLink
    }
};
